import { type HTMLAttributes, type Ref, useId } from "react";

import { tv, type VariantProps } from "tailwind-variants";

import { cn } from "@virtual-brain-lib/frontend-design-system/utils";

import { Tooltip, type TooltipProps } from "./Tooltip";

export const spanVariants = tv({
	base: "",
	variants: {
		color: {
			default: "text-default",
			defaultLight: "text-default/65",
			primary: "text-on-primary",
			danger: "text-on-danger",
			success: "text-on-success",
			warning: "text-warning",
			info: "text-info",
		},
		variant: {
			"title-xl": "font-title text-3xl leading-12 font-semibold",
			"title-lg": "font-title text-2xl leading-9 font-medium",
			"title-md": "font-title text-xl leading-7.5 font-normal",
			"title-sm": "font-title text-lg leading-6 font-normal",
			"title-xs": "font-title text-base leading-5 font-normal",
			"title-2xs": "font-title text-sm leading-4 font-normal",
			"body-xl": "font-body text-xl leading-6 font-normal",
			"body-lg": "font-body text-lg leading-4.5 font-normal",
			"body-md": "font-body text-base leading-4.5 font-normal",
			"body-sm": "font-body text-sm font-normal",
			"body-xs": "font-body text-xs font-normal",
			"body-2xs": "font-body text-[10px] font-normal",
			"button-xl": "font-title text-2xl leading-6",
			"button-lg": "font-title text-lg leading-4.5",
			"button-md": "font-title text-base leading-4",
			"button-sm": "leading-auto font-title text-sm",
			"button-xs": "font-title text-xs leading-2",
			"input-xl": "font-body text-2xl leading-6 font-medium",
			"input-lg": "font-body text-lg leading-4.5 font-medium",
			"input-md": "font-body text-base leading-4 font-medium",
			"tag-xs": "text-[8px]",
			"tag-sm": "text-[10px]",
			"tag-md": "text-xs",
			"tag-lg": "text-sm",
			"tag-xl": "text-base",
		},
	},
});

export type SpanVariant = VariantProps<typeof spanVariants>;

export type SpanProps = SpanVariant &
	HTMLAttributes<HTMLSpanElement> & {
		readonly tooltip?: Omit<TooltipProps, "anchor">;
		readonly ref?: Ref<HTMLSpanElement>;
		readonly isBlock?: boolean;
	};

export function Span({
	variant = "body-xs",
	className,
	tooltip,
	color = "default",
	isBlock = false,
	ref,
	...props
}: SpanProps) {
	const spanClassName = spanVariants({
		variant,
		className: cn(className, isBlock && "block"),
		color,
	});
	const tooltipId = useId();

	return (
		<>
			<span
				ref={ref}
				data-tooltip={tooltip ? tooltipId : undefined}
				className={spanClassName}
				{...props}
			/>
			{tooltip && <Tooltip anchor={tooltipId} {...tooltip} />}
		</>
	);
}
